'use client';

import { useState, useEffect } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { FcGoogle } from 'react-icons/fc';

import FormEmailAndPassword from '@/features/auth/components/FormEmailAndPasswordBetterAuth';
import { messages } from '@/features/auth/localization/texts';
import { core } from '@/features/v2/common/login/localization/core';
import { routes } from '@/features/v2/constants/routes';
import { errors } from '@/localization';
import { userUIStore } from '@/stores/userUIStore';

import { useAuth } from '@/core/auth/client/AuthProvider';

import ForgotPassword from '../../../auth/components/ForgotPassword';

const Login = () => {
  const router = useRouter();
  const { setAlert } = userUIStore();
  const [loading, setLoading] = useState<boolean>(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const { signInWithGoogle, session } = useAuth();

  // Redirect if already authenticated
  useEffect(() => {
    if (session?.user) {
      router.push(routes.HOME);
    }
  }, [session, router]);

  const handleGoogleSignIn = async () => {
    setLoading(true);

    try {
      await signInWithGoogle();
      setLoading(false);
      router.push(routes.HOME);
    } catch (error) {
      setLoading(false);
      setAlert({
        message: errors.tryAgain,
        type: 'error',
        show: true,
        duration: 5000,
      });
    }
  };

  const handleLoginSuccess = () => {
    router.push(routes.HOME);
  };

  const handleLoginError = (error?: string) => {
    setAlert({
      message: error || errors.tryAgain,
      type: 'error',
      show: true,
      duration: 5000,
    });
  };

  const toggleForgotPassword = () => {
    setShowForgotPassword(!showForgotPassword);
  };

  const handleBackFromForgotPassword = () => {
    setShowForgotPassword(false);
  };

  return (
    <div className="w-full max-w-[500px] mx-auto rounded-lg py-4 px-4 sm:px-6 z-30">
      <div className="z-50!">
        <h3 className="bg-linear-to-br from-primary-300 to-primary-600 text-transparent bg-clip-text text-3xl font-bold text-center font-title">
          {core.title}
        </h3>
        <p className="text-center text-sm mt-2 font-text z-50">
          {core.noAccount}
          <span className="ms-2 text-gradient-50 border-b border-gradient-50 leading-none">
            <Link href={routes.REGISTER}>
              {core.buttonRegister}
            </Link>
          </span>
        </p>
        {showForgotPassword ? (
          <ForgotPassword onBack={handleBackFromForgotPassword} />
        ) : (
          <div className="mt-6 font-text z-30 w-full">
            <FormEmailAndPassword isRegister={false} onSuccess={handleLoginSuccess} onSuccessWithOtp={() => {}} onError={handleLoginError} />
            <div className="flex items-center justify-center gap-2 mt-1 pb-8">
              <div className="flex flex-col items-center gap-2">
                <button
                  type="button"
                  aria-label={core.buttonGoogle}
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  className="bg-white rounded-full w-10 h-10 flex items-center justify-center outline-hidden focus:outline-hidden focus:border-none disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FcGoogle className="text-2xl" aria-hidden />
                </button>
                <p>{core.buttonGoogle}</p>
              </div>
            </div>
            <div className="text-center mt-4 items-center z-50">
              <button
                type="button"
                onClick={toggleForgotPassword}
                className="text-default-900 hover:underline"
              >
                {messages.forgotPassword}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
